/**
 * CLI Utilities Commands — a freeform agent toolkit.
 *
 * Three primitives for building and sharing reports:
 *   - object: upload images/videos/files to the CDN (public or private), with
 *             optional TTL, and get shareable / signed links.
 *   - gist:   publish text/markdown viewable in the Cofactor web app.
 *   - email / slack: send one-off messages to ORG MEMBERS (looked up by email
 *             or name). Use `cofactor org members` to see who can receive them.
 *
 * @module utilities
 */

import fs from 'node:fs/promises';
import { writeRawOutput } from './brand.js';
import path from 'node:path';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

// ── shared helpers ─────────────────────────────────────────────────────────

/** Collect every value for a repeated flag, e.g. `--to a --to b`. */
function getAllArgValues(argv: string[], key: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === key && i + 1 < argv.length) out.push(argv[i + 1]!);
  }
  return out;
}

/** Parse a human duration (`30s`, `15m`, `24h`, `7d`, or bare seconds) → seconds. */
function parseTtlSeconds(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const m = raw.trim().match(/^(\d+)\s*(s|m|h|d)?$/i);
  if (!m) {
    console.error(`Error: invalid --ttl '${raw}'. Use e.g. 30s, 15m, 24h, 7d, or seconds.`);
    process.exit(1);
  }
  const n = Number(m[1]);
  const unit = (m[2] ?? 's').toLowerCase();
  const mult = unit === 'd' ? 86400 : unit === 'h' ? 3600 : unit === 'm' ? 60 : 1;
  return n * mult;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.from(chunk));
  return Buffer.concat(chunks).toString('utf-8');
}

/** Resolve message/gist body from --body, --body-file, or --stdin. */
async function resolveBody(argv: string[]): Promise<string> {
  const inline = getArgValue(argv, '--body');
  if (inline !== undefined) return inline;
  const file = getArgValue(argv, '--body-file');
  if (file) {
    try {
      return await fs.readFile(file, 'utf-8');
    } catch (err) {
      console.error(`Error: failed to read ${file}: ${err instanceof Error ? err.message : err}`);
      process.exit(1);
    }
  }
  if (hasFlag(argv, '--stdin')) return readStdin();
  console.error('Error: provide a body via --body <text>, --body-file <path>, or --stdin.');
  process.exit(1);
}

// ── help ───────────────────────────────────────────────────────────────────

function printHelp(): void {
  console.log(
    [
      'Usage: cofactor utilities <group> <sub-command> [options]',
      '',
      'A freeform toolkit for building and sharing reports.',
      '',
      'Objects (images / videos / files):',
      '  object upload <file> [--public] [--ttl 7d] [--name <n>] [--json]',
      '  object list [--kind image|video|data|all] [--search <q>] [--json]',
      '  object url <id> [--download]      Print a shareable/signed link',
      '  object delete <id>',
      '',
      'Gists (text / markdown, viewable in the web app):',
      '  gist create [<file> | --stdin] [--title <t>] [--format markdown|text]',
      '                                   [--public] [--ttl 7d] [--json]',
      '  gist list [--search <q>] [--json]',
      '  gist get <id> [--raw]            --raw prints content; default shows metadata + link',
      '  gist delete <id>',
      '',
      'Messaging (org members only — see `cofactor org members`):',
      '  email --to <member> [--to ...] --subject <s> (--body <t> | --body-file <f> | --stdin)',
      '  slack --to <member> [--to ...] (--body <t> | --body-file <f> | --stdin)',
      '',
      'Common options:',
      '  --env <prod|dev|local>   Target environment',
      '  --json                   Machine-readable output',
      '',
      'Recipe — build & share a report:',
      '  1. Upload evidence:   cofactor utilities object upload chart.png --public',
      '  2. Write a report:    cofactor utilities gist create report.md --public --title "Run 42"',
      '     (embed the object URLs from step 1 as markdown images)',
      '  3. Share the link:    cofactor utilities slack --to alex@acme.com --body "Report: <gist url>"',
    ].join('\n')
  );
}

// ── objects ──────────────────────────────────────────────────────────────

type ObjectItem = {
  id: string;
  visibility: 'public' | 'private';
  filename: string;
  contentType: string;
  sizeBytes: number;
  expiresAt: string | null;
  createdAt: string;
  publicUrl: string | null;
};

async function objectUpload(argv: string[], apiUrl: string, token: string): Promise<void> {
  const filePath = argv.find((a) => !a.startsWith('-'));
  if (!filePath) {
    console.error('Error: missing file path. Usage: utilities object upload <file> [--public] [--ttl 7d]');
    process.exit(1);
  }
  const isPublic = hasFlag(argv, '--public');
  const ttlSeconds = parseTtlSeconds(getArgValue(argv, '--ttl'));
  const overrideName = getArgValue(argv, '--name');
  const jsonOutput = hasFlag(argv, '--json');

  let buf: Buffer;
  try {
    buf = await fs.readFile(filePath);
  } catch (err) {
    console.error(`Error: failed to read ${filePath}: ${err instanceof Error ? err.message : err}`);
    process.exit(1);
  }
  const filename = overrideName ?? path.basename(filePath);

  const form = new FormData();
  form.append('file', new Blob([new Uint8Array(buf)]), filename);
  form.append('visibility', isPublic ? 'public' : 'private');
  if (ttlSeconds) form.append('ttlSeconds', String(ttlSeconds));
  if (overrideName) form.append('filename', overrideName);

  const res = await fetch(`${apiUrl}/org/utilities/objects`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (res.status === 401) {
    console.error('Error: Unauthorized. Run: cofactor login');
    process.exit(1);
  }
  let body: { ok: boolean; error?: string; data?: ObjectItem & { url: string } };
  try {
    body = (await res.json()) as typeof body;
  } catch {
    console.error(`Error: invalid JSON response (${res.status})`);
    process.exit(1);
  }
  if (!body.ok || !body.data) {
    console.error(`Error: ${body.error ?? `upload failed (${res.status})`}`);
    process.exit(1);
  }

  const created = body.data;
  if (jsonOutput) {
    console.log(JSON.stringify(created, null, 2));
    return;
  }
  console.log(`✓ Uploaded ${created.filename} (${created.visibility})`);
  console.log(`  ID:   ${created.id}`);
  console.log(`  Type: ${created.contentType}  Size: ${formatSize(created.sizeBytes)}`);
  if (created.expiresAt) console.log(`  Expires: ${created.expiresAt}`);
  console.log(`  Link: ${created.url}`);
  if (created.visibility === 'private') {
    console.log('  (private link is short-lived — re-mint with: utilities object url ' + created.id + ')');
  }
}

async function objectList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const params = new URLSearchParams();
  const kind = getArgValue(argv, '--kind');
  const search = getArgValue(argv, '--search');
  if (kind) params.set('kind', kind);
  if (search) params.set('search', search);
  const qs = params.toString();

  const result = await apiRequest<{ ok: boolean; error?: string; data: ObjectItem[] }>(
    apiUrl,
    token,
    'GET',
    `/org/utilities/objects${qs ? `?${qs}` : ''}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  if (result.data.length === 0) {
    console.log('No objects.');
    return;
  }
  for (const o of result.data) {
    console.log(`${o.id}  ${o.visibility.padEnd(7)}  ${formatSize(o.sizeBytes).padStart(8)}  ${o.filename}`);
  }
}

async function objectUrl(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing object id. Usage: utilities object url <id> [--download]');
    process.exit(1);
  }
  const dl = hasFlag(argv, '--download') ? '?dl=1' : '';
  const result = await apiRequest<{ ok: boolean; error?: string; url?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/utilities/objects/${id}/url${dl}`
  );
  if (!result.ok || !result.url) {
    console.error(`Error: ${result.error ?? 'failed to get URL'}`);
    process.exit(1);
  }
  console.log(result.url);
}

async function objectDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing object id. Usage: utilities object delete <id>');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/utilities/objects/${id}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`✓ Deleted ${id}`);
}

// ── gists ────────────────────────────────────────────────────────────────

type GistItem = {
  id: string;
  slug: string;
  title: string;
  content?: string;
  format: 'text' | 'markdown';
  visibility: 'public' | 'private';
  expiresAt: string | null;
  createdAt: string;
  viewUrl?: string | null;
};

async function gistCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const filePath = argv.find((a) => !a.startsWith('-'));
  let content: string;
  if (filePath) {
    try {
      content = await fs.readFile(filePath, 'utf-8');
    } catch (err) {
      console.error(`Error: failed to read ${filePath}: ${err instanceof Error ? err.message : err}`);
      process.exit(1);
    }
  } else if (hasFlag(argv, '--stdin')) {
    content = await readStdin();
  } else {
    const inline = getArgValue(argv, '--content');
    if (inline === undefined) {
      console.error('Error: provide content via a <file> arg, --stdin, or --content <text>.');
      process.exit(1);
    }
    content = inline;
  }

  const title =
    getArgValue(argv, '--title') ?? (filePath ? path.basename(filePath) : 'Untitled gist');
  const format = (getArgValue(argv, '--format') as 'text' | 'markdown') ?? 'markdown';
  const visibility = hasFlag(argv, '--public') ? 'public' : 'private';
  const ttlSeconds = parseTtlSeconds(getArgValue(argv, '--ttl'));

  const result = await apiRequest<{ ok: boolean; error?: string; data?: GistItem }>(
    apiUrl,
    token,
    'POST',
    '/org/utilities/gists',
    { title, content, format, visibility, ...(ttlSeconds ? { ttlSeconds } : {}) }
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'failed to create gist'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  console.log(`✓ Created gist "${result.data.title}" (${result.data.visibility})`);
  console.log(`  ID: ${result.data.id}`);
  if (result.data.viewUrl) console.log(`  Public link: ${result.data.viewUrl}`);
  else console.log('  View in the web app under Utilities → Gists.');
}

async function gistList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const search = getArgValue(argv, '--search');
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const result = await apiRequest<{ ok: boolean; error?: string; data: GistItem[] }>(
    apiUrl,
    token,
    'GET',
    `/org/utilities/gists${qs}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  if (result.data.length === 0) {
    console.log('No gists.');
    return;
  }
  for (const g of result.data) {
    console.log(`${g.id}  ${g.visibility.padEnd(7)}  ${g.format.padEnd(8)}  ${g.title}`);
  }
}

async function gistGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing gist id. Usage: utilities gist get <id> [--raw]');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string; data?: GistItem }>(
    apiUrl,
    token,
    'GET',
    `/org/utilities/gists/${id}`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'gist not found'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--raw')) {
    process.stdout.write(result.data.content ?? '');
    return;
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  // Title, id, link and body are all stored customer content — render raw.
  writeRawOutput(`${result.data.title} (${result.data.visibility}, ${result.data.format})`);
  writeRawOutput(`  ID: ${result.data.id}`);
  if (result.data.viewUrl) writeRawOutput(`  Public link: ${result.data.viewUrl}`);
  writeRawOutput('');
  writeRawOutput(result.data.content ?? '');
}

async function gistDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing gist id. Usage: utilities gist delete <id>');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/utilities/gists/${id}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`✓ Deleted ${id}`);
}

// ── messaging ──────────────────────────────────────────────────────────────

async function sendEmail(argv: string[], apiUrl: string, token: string): Promise<void> {
  const recipients = getAllArgValues(argv, '--to');
  const subject = getArgValue(argv, '--subject');
  if (recipients.length === 0 || !subject) {
    console.error('Error: usage: utilities email --to <member> [--to ...] --subject <s> (--body <t> | --body-file <f> | --stdin)');
    process.exit(1);
  }
  const body = await resolveBody(argv);

  const result = await apiRequest<{ ok: boolean; error?: string; data?: { sent: number } }>(
    apiUrl,
    token,
    'POST',
    '/org/utilities/email',
    { recipients, subject, body }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`✓ Email sent to ${result.data?.sent ?? recipients.length} recipient(s).`);
}

async function sendSlack(argv: string[], apiUrl: string, token: string): Promise<void> {
  const recipients = getAllArgValues(argv, '--to');
  if (recipients.length === 0) {
    console.error('Error: usage: utilities slack --to <member> [--to ...] (--body <t> | --body-file <f> | --stdin)');
    process.exit(1);
  }
  const body = await resolveBody(argv);

  const result = await apiRequest<{ ok: boolean; error?: string; data?: { sent: number } }>(
    apiUrl,
    token,
    'POST',
    '/org/utilities/slack',
    { recipients, body }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`✓ Slack message sent to ${result.data?.sent ?? recipients.length} recipient(s).`);
}

// ── dispatch ─────────────────────────────────────────────────────────────

async function runObject(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [sub, ...rest] = argv;
  switch (sub) {
    case 'upload':
      return objectUpload(rest, apiUrl, token);
    case 'list':
      return objectList(rest, apiUrl, token);
    case 'url':
      return objectUrl(rest, apiUrl, token);
    case 'delete':
      return objectDelete(rest, apiUrl, token);
    default:
      console.error(`Unknown object sub-command: ${sub ?? '(none)'}`);
      printHelp();
      process.exit(1);
  }
}

async function runGist(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [sub, ...rest] = argv;
  switch (sub) {
    case 'create':
      return gistCreate(rest, apiUrl, token);
    case 'list':
      return gistList(rest, apiUrl, token);
    case 'get':
      return gistGet(rest, apiUrl, token);
    case 'delete':
      return gistDelete(rest, apiUrl, token);
    default:
      console.error(`Unknown gist sub-command: ${sub ?? '(none)'}`);
      printHelp();
      process.exit(1);
  }
}

export async function runUtilities(argv: string[]): Promise<void> {
  const [group, ...rest] = argv;

  if (!group || group === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (group) {
    case 'object':
      return runObject(rest, apiUrl, token);
    case 'gist':
      return runGist(rest, apiUrl, token);
    case 'email':
      return sendEmail(rest, apiUrl, token);
    case 'slack':
      return sendSlack(rest, apiUrl, token);
    default:
      console.error(`Unknown utilities group: ${group}`);
      printHelp();
      process.exit(1);
  }
}
